import { getInternalTicketWorkflowByTicketId } from '@/modules/Service/services/ticket'
import {
    InternalTicketWorkflowStatus,
    InternalTicketWorkflowStepName,
    Service,
    TicketWorkflowStep,
} from '@/modules/Service/types'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Chip, IconButton, Typogaphy } from 'vnetwork-ui'
import RegisterService from '@/modules/Service/components/Detail/TabComponents/Information/InternalTicketWorkflow/RegisterService'
import CSKHVerificationForm from '@/modules/Service/components/Detail/TabComponents/Information/InternalTicketWorkflow/CSKHVerificationForm'
import FinanceVerificationForm from '@/modules/Service/components/Detail/TabComponents/Information/InternalTicketWorkflow/FinanceVerificationForm'
import { formatDateWithFormatter } from '@/utils/function'
import EyeIcons from '@/icons/EyeIcons'
import CloseIcons from '@/icons/CloseIcons'
import ConfirmServiceIcons from '@/icons/ConfirmServiceIcons'
import LoaderIcons from '@/icons/LoaderIcons'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Settings } from 'lucide-react'

interface Props {
    ticketId: number
    service: Service
}

const InternalTicketWorkflowByTicket: React.FC<Props> = ({
    ticketId,
    service,
}) => {
    const { t } = useTranslation()
    const [expandedStep, setExpandedStep] = useState<number | null>(null)
    const [forceFullView, setForceFullView] = useState(false)

    // API returns single object, not array
    const { data: ticket, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INTERNAL, 'tickets', 'by-ticket', ticketId],
        queryFn: () => getInternalTicketWorkflowByTicketId(ticketId),
        enabled: !!ticketId,
    })

    // Define active statuses where we show full workflow
    const activeStatuses = ['opening', 'work-in-progress', 'pending']

    // Only show Tech-related steps
    const techStepNames = [
        InternalTicketWorkflowStepName.TechnicalCreateService.toLowerCase(),
        InternalTicketWorkflowStepName.TechnicalCancelService.toLowerCase(),
        InternalTicketWorkflowStepName.TechKhoiTaoTrenDC.toLowerCase(),
    ]

    // Filter to only Tech steps
    const techSteps =
        ticket?.ticket_workflow_steps?.filter((step) =>
            techStepNames.includes(step.step_name?.toLowerCase())
        ) || []

    // Auto-expand step when workflow is active
    React.useEffect(() => {
        if (techSteps.length > 0) {
            const isActiveWorkflow =
                activeStatuses.includes(ticket?.ticket_status || '') ||
                forceFullView

            if (isActiveWorkflow && !expandedStep) {
                // First priority: find any "doing" step
                const doingStep = techSteps.find(
                    (step) => step.status === InternalTicketWorkflowStatus.Doing
                )

                if (doingStep) {
                    setExpandedStep(doingStep.step_id)
                } else {
                    // Second priority: find first incomplete step (not done)
                    const incompleteStep = techSteps
                        .sort((a: any, b: any) => a.step_order - b.step_order)
                        .find(
                            (step) =>
                                step.status !==
                                InternalTicketWorkflowStatus.Done
                        )

                    if (incompleteStep) {
                        setExpandedStep(incompleteStep.step_id)
                    } else {
                        // Last resort: expand first step if all are complete
                        const firstStep = techSteps.sort(
                            (a: any, b: any) => a.step_order - b.step_order
                        )?.[0]
                        if (firstStep) {
                            setExpandedStep(firstStep.step_id)
                        }
                    }
                }
            }
        }
    }, [
        techSteps,
        ticket?.ticket_status,
        expandedStep,
        forceFullView,
        activeStatuses,
    ])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    // No Tech workflow steps
    if (!ticket || techSteps.length === 0) {
        return null
    }

    const isActiveWorkflow =
        activeStatuses.includes(ticket.ticket_status) || forceFullView

    const handleStepClick = (stepId: number) => {
        setExpandedStep(expandedStep === stepId ? null : stepId)
    }

    // Compact view for non-active tickets
    if (!isActiveWorkflow) {
        return (
            <div className="flex justify-end mt-2">
                <div
                    className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-xs cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => setForceFullView(true)}
                >
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-600 font-medium">
                        {t('services:internal_workflow')}
                    </span>
                    <Chip
                        label={t(
                            `services:internal_tickets.${ticket.ticket_type_key}`
                        )}
                        color="default"
                        size="small"
                        variant="outlined"
                    />
                    <span className="text-gray-500">
                        (
                        {t(
                            `services:internal_tickets.status.${ticket.ticket_status}`
                        )}
                        )
                    </span>
                    <span className="text-gray-500 ml-1">
                        {
                            techSteps.filter(
                                (s: any) =>
                                    s.status ===
                                    InternalTicketWorkflowStatus.Done
                            ).length
                        }
                        /{techSteps.length}
                    </span>
                    <IconButton
                        size="small"
                        onClick={() => setForceFullView(true)}
                    >
                        <EyeIcons className="w-3 h-3" />
                    </IconButton>
                </div>
            </div>
        )
    }

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-4">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Settings className="w-4 h-4 text-gray-500" />
                        <Typogaphy className="font-medium text-gray-700 text-sm">
                            {t('services:internal_workflow')}:
                        </Typogaphy>
                        <Chip
                            label={t(
                                `services:internal_tickets.${ticket.ticket_type_key}`
                            )}
                            size="small"
                            variant="outlined"
                        />
                        {service?.service_id && (
                            <Link
                                to={generatePathDatacentral(
                                    paths.serviceDetail,
                                    { id: service.service_id }
                                )}
                                target="_blank"
                                className="text-xs text-gray-500 hover:text-primary hover:underline"
                            >
                                #{service.service_id}
                            </Link>
                        )}
                        {service?.customer_id && (
                            <Link
                                to={generatePathDatacentral(
                                    paths.customerDetail,
                                    { id: service.customer_id }
                                )}
                                target="_blank"
                                className="text-xs text-gray-500 hover:text-primary hover:underline"
                            >
                                @{service.customer_name || service.customer_id}
                            </Link>
                        )}
                    </div>
                    {forceFullView && (
                        <IconButton
                            size="small"
                            onClick={() => setForceFullView(false)}
                        >
                            <CloseIcons className="w-3 h-3" />
                        </IconButton>
                    )}
                </div>

                {/* Progress Steps */}
                <div className="flex items-center justify-between relative px-4">
                    {/* Progress Line */}
                    <div className="absolute top-4 left-8 right-8 h-0.5 bg-gray-200">
                        <div
                            className="h-full bg-gray-500 transition-all duration-300"
                            style={{
                                width: `${(techSteps.filter((s: any) => s.status === InternalTicketWorkflowStatus.Done).length / techSteps.length) * 100}%`,
                            }}
                        />
                    </div>

                    {techSteps
                        .sort((a: any, b: any) => a.step_order - b.step_order)
                        .map((step: TicketWorkflowStep, index: number) => (
                            <div
                                key={step.step_id}
                                className="flex flex-col items-center relative z-10 flex-1"
                            >
                                {/* Step Circle */}
                                <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium cursor-pointer border-2 ${
                                        step.status ===
                                        InternalTicketWorkflowStatus.Done
                                            ? 'bg-gray-700 border-gray-700 text-white'
                                            : step.status ===
                                                InternalTicketWorkflowStatus.Doing
                                              ? 'bg-gray-500 border-gray-500 text-white'
                                              : step.status ===
                                                  InternalTicketWorkflowStatus.Blocked
                                                ? 'bg-gray-400 border-gray-400 text-white'
                                                : 'bg-white border-gray-300 text-gray-500'
                                    }`}
                                    onClick={() =>
                                        handleStepClick(step.step_id)
                                    }
                                >
                                    {step.status ===
                                    InternalTicketWorkflowStatus.Done ? (
                                        <ConfirmServiceIcons className="w-4 h-4" />
                                    ) : step.status ===
                                      InternalTicketWorkflowStatus.Doing ? (
                                        <LoaderIcons className="w-4 h-4" />
                                    ) : step.status ===
                                      InternalTicketWorkflowStatus.Blocked ? (
                                        <span className="text-xs">!</span>
                                    ) : (
                                        <span className="text-xs">
                                            {index + 1}
                                        </span>
                                    )}
                                </div>

                                <div className="mt-2 text-center">
                                    <div className="text-xs font-medium text-gray-700">
                                        {t(
                                            `services:internal_tickets.actions.${step.step_name?.toLowerCase()}`
                                        )}
                                    </div>

                                    {/* Status Indicator */}
                                    <div className="text-xs mt-1 text-gray-500">
                                        {step.status ===
                                        InternalTicketWorkflowStatus.Done
                                            ? t('completed')
                                            : step.status ===
                                                InternalTicketWorkflowStatus.Doing
                                              ? t('processing')
                                              : step.status ===
                                                  InternalTicketWorkflowStatus.Blocked
                                                ? t('blocked')
                                                : t('pending')}
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>

                {/* Step Details */}
                {expandedStep && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                        {techSteps
                            .filter((step) => step.step_id === expandedStep)
                            .map((step) => (
                                <div
                                    key={step.step_id}
                                    className="bg-gray-50 rounded-lg p-4"
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                                        <Typogaphy className="font-medium text-gray-700 text-sm">
                                            {t(
                                                `services:internal_tickets.actions.${step.step_name?.toLowerCase()}`
                                            )}
                                        </Typogaphy>
                                    </div>

                                    {/* Technical Setup Form */}
                                    {step.step_name?.toLowerCase() ===
                                        InternalTicketWorkflowStepName.TechnicalCreateService.toLowerCase() &&
                                        step.status ===
                                            InternalTicketWorkflowStatus.Doing && (
                                            <RegisterService
                                                service={service}
                                                stepId={step.step_id}
                                            />
                                        )}

                                    {/* Technical Cancel Service Form */}
                                    {step.step_name?.toLowerCase() ===
                                        InternalTicketWorkflowStepName.TechnicalCancelService.toLowerCase() &&
                                        step.status ===
                                            InternalTicketWorkflowStatus.Doing && (
                                            <CSKHVerificationForm
                                                stepId={step.step_id}
                                                stepName={step.step_name}
                                            />
                                        )}

                                    {[
                                        InternalTicketWorkflowStepName.CSKHConfirm.toLowerCase(),
                                        InternalTicketWorkflowStepName.CSKHVerifyCreateService.toLowerCase(),
                                        InternalTicketWorkflowStepName.CSKHConfirmCancel.toLowerCase(),
                                        InternalTicketWorkflowStepName.FinalDone.toLowerCase(),
                                        InternalTicketWorkflowStepName.CSKHConfirmTaxPercentageUpdate.toLowerCase(),
                                        // POC workflow steps
                                        InternalTicketWorkflowStepName.CSKHXacNhan.toLowerCase(),
                                        InternalTicketWorkflowStepName.TechKhoiTaoTrenDC.toLowerCase(),
                                        InternalTicketWorkflowStepName.CSKHKiemTraHoanTat.toLowerCase(),
                                    ].includes(
                                        step.step_name?.toLowerCase() as InternalTicketWorkflowStepName
                                    ) &&
                                        step.status ===
                                            InternalTicketWorkflowStatus.Doing && (
                                            <CSKHVerificationForm
                                                stepId={step.step_id}
                                                stepName={step.step_name}
                                                service={service}
                                            />
                                        )}

                                    {step.step_name?.toLowerCase() ===
                                        InternalTicketWorkflowStepName.FinanceApproveTaxPercentageUpdate.toLowerCase() &&
                                        step.status ===
                                            InternalTicketWorkflowStatus.Doing && (
                                            <FinanceVerificationForm
                                                stepId={step.step_id}
                                                stepName={step.step_name}
                                                filledData={step.filled_data}
                                            />
                                        )}

                                    {step.approver_name && (
                                        <div className="mt-3">
                                            <Typogaphy className="text-xs text-gray-600 mb-2">
                                                {t(
                                                    'services:internal_tickets.approver_name'
                                                )}
                                                : {step.approver_name}
                                            </Typogaphy>
                                            <Typogaphy className="text-xs text-gray-600 mb-2">
                                                {t(
                                                    'services:internal_tickets.approver_at'
                                                )}
                                                :{' '}
                                                {formatDateWithFormatter(
                                                    step.approved_at,
                                                    'DD/MM/YYYY HH:mm'
                                                )}
                                            </Typogaphy>
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default InternalTicketWorkflowByTicket
